import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCalendarStatus, unsyncGoogleCalendar } from '../../Redux/ActionCreators/userAction';
import "./user.scss";

const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F75891" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, flexShrink: 0 }}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const Myprofile = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const userId = localStorage.getItem('userId');
    const name = localStorage.getItem('name') || '';
    const email = localStorage.getItem('email') || '';
    const tenantId = localStorage.getItem('tenentId') || '';
    const profileDetails = JSON.parse(localStorage.getItem('profileDetails') || '{}');
    const meDetails = useSelector((state) => state.auth.meDetails);

    const memberSince = (profileDetails?.createdDate || profileDetails?.createdAt)
        ? new Date(profileDetails?.createdDate || profileDetails?.createdAt)
            .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : null;

    const [calendarSynced, setCalendarSynced] = useState(null);
    const [syncedEmail, setSyncedEmail] = useState('');
    const [statusLoading, setStatusLoading] = useState(true);
    const [unsyncLoading, setUnsyncLoading] = useState(false);
    const [alertMsg, setAlertMsg] = useState({ text: '', type: '' });


    useEffect(() => {
        if (location.state?.calendarSuccess) {
            setAlertMsg({ text: location.state.calendarSuccess, type: 'success' });
            navigate(location.pathname, { replace: true, state: {} });
        } else if (location.state?.calendarError) {
            setAlertMsg({ text: location.state.calendarError, type: 'error' });
            navigate(location.pathname, { replace: true, state: {} });
        }

        const checkStatus = async () => {
            try {
                setStatusLoading(true);
                const response = await dispatch(getCalendarStatus(userId));
                const data = response?.data?.data;
                const synced = data?.status === 'connected';
                setCalendarSynced(synced);
                setSyncedEmail(synced ? (data?.provider_account_email || '') : '');
            } catch {
                setCalendarSynced(false);
                setSyncedEmail('');
            } finally {
                setStatusLoading(false);
            }
        };
        checkStatus();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSync = () => {
        localStorage.setItem('googleAuthSource', 'calendarSync');
        const clientId = process.env.REACT_APP_GOOGLE_CALENDAR_CLIENT_ID;
        const redirectUri = encodeURIComponent(window.location.origin + '/auth/google/callback');
        const scope = encodeURIComponent('https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly openid email profile');
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&access_type=offline&prompt=consent`;
    };

    const handleUnsync = async () => {
        setUnsyncLoading(true);
        setAlertMsg({ text: '', type: '' });
        try {
            const response = await dispatch(unsyncGoogleCalendar(userId));
            if (response?.status === 200) {
                setCalendarSynced(false);
                setSyncedEmail('');
                setAlertMsg({ text: 'Google Calendar unsynced successfully.', type: 'success' });
            } else {
                setAlertMsg({ text: 'Failed to unsync Google Calendar. Please try again.', type: 'error' });
            }
        } finally {
            setUnsyncLoading(false);
        }
    };

    const firstChar = name.charAt(0).toUpperCase();
    const secondChar = name.charAt(1)?.toUpperCase();
    const initial = firstChar.trim() ? firstChar : (secondChar || 'U');


    return (
        <div className='d-flex justify-content-center'>
            <div className="myprofile-container">
                {statusLoading ? (
                    <div className="loading-overlay">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                <>
                <h2 className="myprofile-title">My Profile</h2>

                {/* Profile Header Card */}
                <div className="myprofile-card profile-header-card">
                    <div className="profile-avatar">{initial}</div>
                    <div className="profile-name-block">
                        <h3 className="profile-name">{name}</h3>
                        <span className="detail-value email-value">{email}</span>
                        {memberSince && (
                            <p className="member-since">Member since {memberSince}</p>
                        )}
                    </div>
                    <span className="role-badge">{meDetails?.companies?.[0]?.company_user?.roles?.[0]?.name || 'Recruitment specialist'}</span>
                </div>

                {/* Details Grid */}
                {/* <div className="profile-details-grid">
                    <div className="profile-detail-card">
                        <span className="detail-label">NAME</span>
                        <span className="detail-value">{name}</span>
                    </div>
                    <div className="profile-detail-card">
                        <span className="detail-label">EMAIL</span>
                        <span className="detail-value email-value">{email}</span>
                    </div>
                    <div className="profile-detail-card">
                        <span className="detail-label">ROLE</span>
                        <span className="detail-value">{meDetails?.companies?.[0]?.company_user?.roles?.[0]?.name || 'Recruitment specialist'}</span>
                    </div>
                    <div className="profile-detail-card">
                        <span className="detail-label">TENANT ID</span>
                        <span className="detail-value tenant-id-value">{tenantId}</span>
                    </div>
                </div> */}

                {/* Calendar Integration */}
                <div className="myprofile-card profile-calendar-card">
                    <span className="detail-label">CALENDAR INTEGRATION</span>

                    {alertMsg.text && (
                        <div className={`calendar-alert ${alertMsg.type}`}>
                            {alertMsg.text}
                        </div>
                    )}

                    <div className="calendar-btns">
                        {calendarSynced && syncedEmail && (
                            <span className="calendar-synced-email">
                                Synced with {syncedEmail}
                            </span>
                        )}
                        <button
                            className={`calendar-btn${calendarSynced ? ' unsync-btn' : ''}`}
                            onClick={calendarSynced ? handleUnsync : handleSync}
                            disabled={statusLoading || unsyncLoading}
                            title={calendarSynced ? 'Unsync Google Calendar' : 'Sync Google Calendar'}
                        >
                            {unsyncLoading ? (
                                <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                                <span className="calendar-btn-text">
                                    <CalendarIcon />
                                    {calendarSynced ? 'Google Calendar Unsync' : 'Google Calendar Sync'}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default Myprofile;
